import { AuthActionCreators, IGet } from '@nkr/store/authorization';
import { useAppHookContext } from '@nkr/utils/rest-api';
import { exportToXlsx, SheetConfig } from '@nkr/utils/exportToExcel';
import { useEffect, useState } from 'react';
import * as moment from 'moment';
import useCoachList from './use-coach-list';
import { IMonthColumn, buildMonthColumns } from './use-assignments-by-center';

interface INewInactiveByReasonRow {
    eventType: number;
    month: string;
    newInactive: number;
}

export interface IInactiveReasonDefinition {
    eventType: number;
    label: string;
}

export interface IInactiveByReasonPivot {
    eventType: number;
    months: { [key: string]: number };
}

const pivot = (rows: INewInactiveByReasonRow[]): IInactiveByReasonPivot[] => {
    const map = rows.reduce((acc, row) => {
        const key = moment(row.month).format('YYYY-MM');
        if (!acc.has(row.eventType)) {
            acc.set(row.eventType, { eventType: row.eventType, months: {} });
        }
        acc.get(row.eventType).months[key] = row.newInactive;
        return acc;
    }, new Map<number, IInactiveByReasonPivot>());

    return Array.from(map.values());
}

interface IRetVal {
    data: IInactiveByReasonPivot[] | null;
    reasons: IInactiveReasonDefinition[];
    columns: IMonthColumn[];
    loading: boolean;
    error: string;
    exportData: () => void;
}

const useNewInactiveByReason = (isSuperAdmin: boolean, year: number, coachId: number, enabled = false): IRetVal => {
    const { dispatch } = useAppHookContext();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [data, setData] = useState<IInactiveByReasonPivot[] | null>(null);
    const [reasons, setReasons] = useState<IInactiveReasonDefinition[]>([]);
    const { getCoachName } = useCoachList();

    const columns = buildMonthColumns(year);

    useEffect(() => {
        if (!enabled) return;
        setLoading(true);
        setError('');

        const url = isSuperAdmin
            ? `${__API__}/admin/microsites/report/new-inactive-by-reason?year=${year}&coachContactId=${coachId}`
            : `${__API__}/microsite/coach/report/new-inactive-by-reason?year=${year}`;

        const config: IGet = { url };

        const fetchData = async () => {
            try {
                const response = await dispatch(AuthActionCreators.apiGet(config));
                if (response && response.data.success) {
                    setData(pivot(response.data.results));
                    setReasons(response.data.reasons);
                } else {
                    setError('Unable to load inactive by reason data');
                    setData(null);
                }
            } catch (err) {
                setData(null);
                setError(err?.message || 'An unexpected error occurred');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [isSuperAdmin, year, coachId, enabled]);

    const exportData = (): void => {
        if (!data) return;

        const coachName = isSuperAdmin ? getCoachName(coachId) : null;

        const sheetCols = [
            { key: 'reason', header: 'Reason' },
            ...columns.map(c => ({ key: c.key, header: c.label })),
        ];

        const monthsByEventType = new Map(data.map(p => [p.eventType, p.months]));

        const rows = reasons.map(reason => {
            const row: Record<string, string | number> = { reason: reason.label };
            columns.forEach(c => { row[c.key] = (monthsByEventType.get(reason.eventType) ?? {})[c.key] ?? 0; });
            return row;
        });

        const sheet: SheetConfig = { name: 'Inactive by Reason', columns: sheetCols, data: rows };
        exportToXlsx(`New Inactive by Reason${coachName ? ` - ${coachName}` : ''} - ${year}.xlsx`, [sheet]);
    };

    return { data, reasons, columns, loading, error, exportData };
}

export default useNewInactiveByReason;
